<template>
  <div class="lw-p-dispatch">
    <LwPageHeader title="Dispatch">
      <template #actions>
        <!--
          A real <a href> (LwButton's href branch, LwButton.vue's
          "element rule" comment), not the <span class="btn"> family
          audit_web.md 3.4 documents everywhere else. The router runs in
          HISTORY mode (router/index.js), so a bare anchor click would force
          a full page reload; the click handler below mirrors vue-router's
          own Link.js guard so a plain click still takes the SPA path while
          middle-click / cmd-click / ctrl-click keep the browser's native
          "open in a new tab" behaviour instead of being hijacked.
        -->
        <LwButton
          variant="primary"
          :href="createLoadHref"
          data-tour="dispatch.create-load"
          @click="handleCreateLoadClick"
        >
          Create load
        </LwButton>
        <!--
          Carrier workflows Phase 2: a broker's rate confirmation becomes a
          load through review. Secondary, after the primary, and the same
          anchor-plus-guard shape so a modified click still opens a tab.
        -->
        <LwButton
          variant="secondary"
          :href="importLoadHref"
          data-tour="dispatch.import"
          @click="handleImportLoadClick"
        >
          Import load
        </LwButton>
      </template>
    </LwPageHeader>

    <!--
      The two boards used to stack, in-progress above delivered. They are
      tabs now, one board on screen at a time, with the choice in the URL
      (`?tab=in-progress` / `?tab=delivered`, lib/tabQuery.js). Both
      LoadLists stay mounted behind LwTabs' v-show, so the data loading is
      exactly what it was: each list fetches on mount and refetches on the
      shared 'change' bus, and a Mark Delivered on the in-progress tab still
      refreshes the delivered one even while it is hidden.

      Spec 49: Runs is the third tab (`?tab=runs`), for the office roles the
      rail entry it replaces was shown to. Unlike the two boards it is NOT
      mounted until its tab is first shown: the runs list fetches /runs,
      /drivers and /trucks/active on mount, and a dispatcher opening the
      board should not pay for those until they ask for the runs. Once
      mounted it stays mounted, like the boards, so its date range and its
      half-filled New run form survive a switch away and back.
    -->
    <LwTabs
      class="lw-p-dispatch__body"
      :tabs="tabs"
      :model-value="activeTab"
      label="Dispatch sections"
      id-prefix="lw-dispatch"
      @update:model-value="selectTab"
    >
      <template #in-progress>
        <LoadList
          title="Loads in Progress"
          :param="{ delivered: 0 }"
          :events="events"
          empty-title="No loads in progress"
          empty-body="Loads you create show up here until they are marked delivered."
          :custody-by-load-id="custodyByLoadId"
          :stale-after-ms="staleAfterMs"
          :telemetry-failed="telemetryFailed"
          :show-telemetry="true"
          @count="counts['in-progress'] = $event"
        />
      </template>

      <!--
        LoadList never declared a `filter` prop (it reads `param`, `events`,
        `title` and nothing else), so the `:filter="{type:['tank','van']}"`
        this page used to pass on both tables did nothing at all. Dropped
        rather than carried forward for the sake of matching yesterday's
        markup.
      -->
      <template #delivered>
        <LoadList
          title="Delivered Loads"
          :param="{ delivered: 1, billed: 0 }"
          :events="events"
          empty-title="No delivered loads waiting to be billed"
          empty-body="A load lands here when it is marked delivered, and leaves once it is billed."
          :custody-by-load-id="custodyByLoadId"
          :stale-after-ms="staleAfterMs"
          :telemetry-failed="telemetryFailed"
          @count="counts.delivered = $event"
        />
      </template>

      <template v-if="canSeeRuns" #runs>
        <Runs v-if="runsMounted" @count="counts.runs = $event" />
      </template>
    </LwTabs>
  </div>
</template>

<script>
import { markRaw } from 'vue';
import { createEmitter } from '../../lib/emitter';
import LoadList from './LoadList.vue';
import Runs from '../Runs/Runs.vue';
import LwPageHeader from '../../components/lw/LwPageHeader.vue';
import LwButton from '../../components/lw/LwButton.vue';
import LwTabs from '../../components/lw/LwTabs.vue';
import { tabQueryMixin } from '../../lib/tabQuery';
import { currentRole } from '../../lib/roles';
import { OFFICE } from '../../router/sections';

// Tab ids double as `?tab=` values. Labels are the lists' own titles.
const TABS = [
  { id: 'in-progress', label: 'Loads in Progress' },
  { id: 'delivered', label: 'Delivered Loads' },
  // Spec 49: the Runs page, as a tab. Gated to the roles its rail entry
  // was (admin, dispatcher, accountant: sections.js's OFFICE).
  { id: 'runs', label: 'Runs', roles: OFFICE },
];

export default {
  name: 'Dispatch',
  components: { LoadList, Runs, LwPageHeader, LwButton, LwTabs },
  mixins: [tabQueryMixin(TABS.map(tab => tab.id))],
  data() {
    return {
      // Each list reports its row total after every fetch (LoadList's
      // `count` event); null until then, which shows no badge.
      counts: { 'in-progress': null, delivered: null, runs: null },
      // Set the first time the Runs tab is shown and never cleared: the
      // runs list mounts lazily (the template comment says why) and then
      // stays, like the two boards.
      runsMounted: false,
      // markRaw because a bus is not state (lib/emitter.js's header says so).
      // Vue 2 got this for free -- `observe()` bailed out on a Vue instance, and
      // the bus WAS one -- but Vue 3 deep-proxies anything plain that `data()`
      // returns, so the emitter would otherwise be handed to both LoadLists as a
      // reactive proxy of itself.
      events: markRaw(createEmitter()),
      // THE TELEMETRY FETCH. Genuinely new behaviour, and deliberately
      // secondary to the board's own /loads fetch that each LoadList still
      // owns. `/tracking/live` is the dispatcher tracking read the live map
      // already uses (boardState.js's header comment); it can arrive late,
      // fail outright, or simply not mention a given load (a delivered load
      // ages out of the "active" set it returns). None of that is a reason
      // to put the whole board into an error state: the board's own /loads
      // fetch is the one thing that gets to do that, because a dispatcher
      // can still work the board with no telemetry at all -- every row falls
      // back to the honest "Not asked" chip boardState.js's telemetryForLoad
      // already produces for a load with no entry. So a failed telemetry
      // fetch below sets custodyByLoadId to null and telemetryFailed to
      // true and nothing else: no toast, no banner, no redirect (contrast
      // audit_web.md 3.8's app.vue:85-111, which hard-redirects to login on
      // ANY network failure with only a console.log -- silence here is a
      // deliberate, narrower choice than that, not the same bug reappearing).
      custodyByLoadId: null,
      staleAfterMs: null,
      telemetryFailed: false,
    };
  },
  computed: {
    // Written defensively like Billing's canSeeFactoring: a store-less
    // mount (the page tests) knows of no user, so it shows no Runs tab,
    // the same fail-closed answer a driver-role login gets. `in` first:
    // reading a missing $store during render is a Vue warning on every
    // store-less mount, and the `in` check is not.
    role() {
      const store = '$store' in this ? this.$store : null;
      return currentRole(store);
    },
    canSeeRuns() {
      return Boolean(this.role && OFFICE.includes(this.role));
    },
    visibleTabs() {
      return TABS.filter(tab => !tab.roles || this.canSeeRuns);
    },
    tabs() {
      return this.visibleTabs.map(tab => ({
        id: tab.id,
        label: tab.label,
        count: this.counts[tab.id],
      }));
    },
    // Overrides tabQueryMixin's: ?tab=runs from someone who cannot see the
    // tab must fall back to the first tab, not select a panel that is not
    // rendered.
    activeTab() {
      const route = '$route' in this ? this.$route : null;
      const query = (route && route.query) || {};
      const value = Array.isArray(query.tab) ? query.tab[0] : query.tab;
      return this.visibleTabs.some(tab => tab.id === value) ? value : this.visibleTabs[0].id;
    },
    createLoadHref() {
      return this.$router.resolve({ name: 'LoadsCreate' }).href;
    },
    importLoadHref() {
      return this.$router.resolve({ name: 'Imports' }).href;
    },
  },
  watch: {
    activeTab: {
      immediate: true,
      handler(tab) {
        if (tab === 'runs') this.runsMounted = true;
      },
    },
  },
  created() {
    this.loadTelemetry();
    // A Mark Delivered / Unmark Delivered / Cancel Load action on either
    // list emits 'change' on this same bus (LoadList.vue); custody and
    // telemetry state can shift the moment that happens, so this page
    // refreshes its own enrichment fetch on the same signal the lists use
    // to refresh their rows.
    this.events.on('change', this.loadTelemetry);
  },
  beforeUnmount() {
    // Today nothing ever unbinds this handler. Left running past
    // beforeUnmount would fire a state write on an instance Vue is about to
    // tear down the moment the next 'change' event arrives.
    this.events.off('change', this.loadTelemetry);
  },
  methods: {
    // Mirrors vue-router's own <router-link> guard (Link.js): a modified
    // click (new tab, new window) or one some other handler already called
    // preventDefault() on is left alone so the browser's native anchor
    // behaviour still applies; everything else is taken over so the router
    // runs in HISTORY mode without a full page reload (a bare `<a href>`
    // would otherwise cause exactly that).
    handleCreateLoadClick(event) {
      if (
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.button !== 0 ||
        event.defaultPrevented
      ) {
        return;
      }
      event.preventDefault();
      this.$router.push({ name: 'LoadsCreate' });
    },
    handleImportLoadClick(event) {
      if (
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.button !== 0 ||
        event.defaultPrevented
      ) {
        return;
      }
      event.preventDefault();
      this.$router.push({ name: 'Imports' });
    },
    loadTelemetry() {
      return this.$http
        .get('/tracking/live')
        .then(res => {
          // Keyed by String(load_id) so a LoadList row can look itself up
          // with String(load.id) regardless of whether the API hands back a
          // number or a numeric string on either side. Built once and
          // assigned wholesale -- never mutated key by key -- so Vue 2's
          // reactivity (which cannot observe a property added to an object
          // after it was made reactive) sees the whole table in one write.
          const byId = {};
          (res.data.loads || []).forEach(entry => {
            byId[String(entry.load_id)] = entry;
          });
          this.custodyByLoadId = byId;
          this.staleAfterMs = res.data.stale_after_ms;
          this.telemetryFailed = false;
        })
        .catch(() => {
          this.custodyByLoadId = null;
          this.telemetryFailed = true;
        });
    },
  },
};
</script>

<style scoped lang="scss">
.lw-p-dispatch {
  // Paper cut 20: nothing in the product is width-constrained on a wide
  // monitor. This is the one number that fixes it for both tables at once.
  max-width: var(--lw-layout-max-content);
}

.lw-p-dispatch__body {
  // Vertical only. The horizontal gutter is already owned by the app shell's
  // `#content` padding (app.scss), and adding a second one here would inset
  // the tabs from the page header sitting directly above them, so the two
  // would no longer line up on their left edge. The gap between the tabs and
  // the board is LwTabs' own panel padding.
  padding: var(--lw-space-4) 0 var(--lw-space-8);
}
</style>
